import {createRouter, createWebHashHistory, RouteRecordRaw} from 'vue-router'
import AdminRoutes from "@/router/admin";
import ProcurementRoutes from "@/router/procurement";
import OperatorRoutes from "@/router/operator";
import InvoiceRoutes from "@/router/invoice";
import ContractRoutes from "@/router/contract";
import UserService from "@/services/user-service";
import {preloadFrontendConfig} from "@/extensions";
import {hasAnyDashboardAccess, hasDashboardAccess} from "@/services/dashboard-access";

type DashboardAccessMeta = 'hub' | 'procurement' | 'invoice' | 'outgoing-invoice' | 'einvoice' | 'ai' | 'supplier'

function getCurrentRoles() {
    return UserService.getCurrentUser()?.jwtUser?.roles ?? []
}

async function isAiEnabled() {
    const configs = await preloadFrontendConfig()
    return !!configs.find((item: any) => item.key === 'ai_enabled')?.value
}

async function canAccessDashboardRoute(access: DashboardAccessMeta) {
    if (access === 'procurement') return hasDashboardAccess(getCurrentRoles(), 'procurement')
    if (access === 'invoice') return hasDashboardAccess(getCurrentRoles(), 'invoice')
    if (access === 'outgoing-invoice') return hasDashboardAccess(getCurrentRoles(), 'outgoingInvoice')
    if (access === 'einvoice') return hasDashboardAccess(getCurrentRoles(), 'einvoice')

    if (access === 'ai') {
        return getCurrentRoles().includes('user-operator') && await isAiEnabled()
    }

    if (access === 'supplier') return hasDashboardAccess(getCurrentRoles(), 'supplier')

    if (access === 'hub') {
        return hasAnyDashboardAccess(getCurrentRoles()) || (getCurrentRoles().includes('user-operator') && await isAiEnabled())
    }

    return true
}

function canAccessOperatorRoute() {
    return getCurrentRoles().includes('user-operator')
}


const routes: Array<RouteRecordRaw> = [
    {
        path: '',
        name: 'index',
        component: () => import('../views/index.vue')
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/login.vue')
    },
    {
        path: '/logout',
        name: 'logout',
        component: () => import('../views/logout.vue')
    },
    {
        path: '/login/:token',
        name: 'loginWithToken',
        component: () => import('../views/login.vue')
    },
    {
        path: '/oauth2/consent',
        name: 'oauth2-consent',
        component: () => import('../views/oauth2-consent.vue')
    },
    {
        path: '/procurement',
        name: 'procurement',
        component: () => import(/* webpackChunkName: "restructre" */ '../views/procurement.vue'),
        children: ProcurementRoutes
    },
    {
        path: '/invoice',
        name: 'invoice',
        component: () => import(/* webpackChunkName: "restructre" */ '../views/invoice.vue'),
        children: InvoiceRoutes
    },
    {
        path: '/contract',
        name: 'contract',
        component: () => import(/* webpackChunkName: "restructre" */ '../views/contract.vue'),
        children: ContractRoutes
    },
    {
        path: '/overview',
        name: 'overview',
        component: () => import(/* webpackChunkName: "restructre" */ '../views/overview.vue')
    },
    {
        path: '/admin',
        name: 'admin',
        component: () => import(/* webpackChunkName: "restructre" */ '../views/admin.vue'),
        children: AdminRoutes
    },
    {
        path: '/operator',
        name: 'operator',
        component: () => import(/* webpackChunkName: "restructre" */ '../views/operator.vue'),
        children: OperatorRoutes
    },
    {
        path: '/profile',
        name: 'profile',
        component: () => import(/* webpackChunkName: "restructre" */ '../views/profile.vue')
    },
    {
        path: '/dashboards',
        name: 'dashboards',
        component: () => import(/* webpackChunkName: "restructre" */ '../views/dashboards.vue'),
        meta: {dashboardAccess: 'hub'}
    },
    {
        path: '/dashboard',
        name: 'dashboard',
        component: () => import(/* webpackChunkName: "restructre" */ '../views/dashboard.vue'),
        meta: {dashboardAccess: 'procurement'}
    },
    {
        path: '/invoice-dashboard',
        name: 'invoice-dashboard',
        component: () => import('../views/invoice-dashboard.vue'),
        meta: {dashboardAccess: 'invoice', invoiceDashboardType: 'incoming'}
    },
    {
        path: '/outgoing-invoice-dashboard',
        name: 'outgoing-invoice-dashboard',
        component: () => import('../views/invoice-dashboard.vue'),
        meta: {dashboardAccess: 'outgoing-invoice', invoiceDashboardType: 'outgoing'}
    },
    {
        path: '/ai-dashboard',
        name: 'ai-dashboard',
        component: () => import('../views/ai-dashboard.vue'),
        meta: {dashboardAccess: 'ai'}
    },
    {
        path: '/supplier-dashboard/:supplierId?',
        name: 'supplier-dashboard',
        component: () => import('../views/supplier-dashboard.vue'),
        meta: {dashboardAccess: 'supplier'}
    },
    {
        path: '/exceptions-center',
        name: 'exceptions-center',
        component: () => import('../views/exceptions-center.vue'),
        meta: {operatorOnly: true}
    },
    {
        path: '/einvoice-dashboard',
        name: 'einvoice-dashboard',
        component: () => import('../views/einvoice-dashboard.vue'),
        meta: {dashboardAccess: 'einvoice'}
    },
    {
        path: '/help',
        name: 'help',
        component: () => import(/* webpackChunkName: "restructre" */ '../views/help.vue')
    },
    {
        path: '/liquidity-planning',
        name: 'liquidity-planning',
        component: () => import('../views/liquidity-planning.vue'),
    },
    {
        path: '/onboarding',
        name: 'onboarding',
        component: () => import(/* webpackChunkName: "restructre" */ '../views/onboarding.vue')
    },
]

const router = createRouter({
    history: createWebHashHistory(process.env.BASE_URL),
    routes
})

router.beforeEach(async (to) => {
    const dashboardAccess = to.meta?.dashboardAccess as DashboardAccessMeta | undefined
    if (!dashboardAccess) return true
    if (await canAccessDashboardRoute(dashboardAccess)) return true
    return {name: 'overview'}
})

router.beforeEach((to) => {
    if (!to.meta?.operatorOnly) return true
    if (canAccessOperatorRoute()) return true
    return {name: 'overview'}
})

export default router
